import React from 'react';
import { Accordion } from '@/shared/components/Accordion';
import { SummaryField } from '@/shared/components/SummaryField';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';
import { EQUIPMENT_RANK_COUNT } from '@/epics/subscriptionManagment/hooks/useCreateSubscription';

export interface PlanPreviewOffer {
  name: string;
  discountLabel: string;
}

export interface PlanPreviewPlanDetails {
  planName: string;
  durationType: string;
  period: string;
  billingCycle: string;
  description: string;
  price: string;
  userCount: string;
  facilityCount: string;
  qmsDocCount: string;
  offer: PlanPreviewOffer | null;
  finalPrice: string;
}

export interface PlanPreviewService {
  key: string;
  serviceName: string;
  usageLimit: string;
  basePrice: string;
  discountLabel: string;
  finalPrice: string;
}

export interface PlanPreviewEquipmentCategory {
  id: string;
  categoryLabel: string;
  basePrice: string;
}

export interface PlanPreviewDiscountTier {
  rank: number;
  discountLabel: string;
}

export interface PlanPreviewData {
  planDetails: PlanPreviewPlanDetails;
  services: PlanPreviewService[];
  equipmentCategories: PlanPreviewEquipmentCategory[];
  discountTiers: PlanPreviewDiscountTier[];
}

export interface PlanPreviewAccordionProps {
  data: PlanPreviewData;
}

/** "Equipment 1" ... "Equipment 7+" — rank 7 is the ceiling covering rank 8+. */
const rankLabel = (rank: number): string => {
  const prefix = SUBSCRIPTION_MANAGEMENT_LABELS.EQUIPMENT_RANK_PREFIX.polish;
  return `${prefix} ${rank}${rank === EQUIPMENT_RANK_COUNT ? '+' : ''}`;
};

/**
 * Read-only accordion summary of a subscription plan, shown on the preview
 * screen before the final Create/Save submit — replaces the old approach of
 * re-rendering the actual step-1..4 form components read-only/disabled.
 */
export const PlanPreviewAccordion: React.FC<PlanPreviewAccordionProps> = ({
  data,
}) => {
  const { planDetails, services, equipmentCategories, discountTiers } = data;

  return (
    <div className="flex flex-col gap-4">
      <Accordion
        badgeNumber="01"
        title="Plan Details"
        testId="preview-plan-details"
      >
        <div className="grid grid-cols-4 gap-6">
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_PLAN_NAME.polish}
            value={planDetails.planName}
          />
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DURATION_TYPE.polish}
            value={planDetails.durationType}
          />
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DURATION.polish}
            value={planDetails.period}
          />
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BILLING_CYCLE.polish}
            value={planDetails.billingCycle}
          />
        </div>
        <div className="grid grid-cols-4 gap-6">
          <SummaryField label="Price" value={planDetails.price} />
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_NUMBER_OF_USERS.polish}
            value={planDetails.userCount}
          />
          <SummaryField
            label={
              SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_NUMBER_OF_FACILITIES.polish
            }
            value={planDetails.facilityCount}
          />
          <SummaryField
            label={
              SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_NUMBER_OF_QMS_DOCUMENTS
                .polish
            }
            value={planDetails.qmsDocCount || '—'}
          />
        </div>
        <div className="grid grid-cols-4 gap-6">
          <SummaryField label="Final Price" value={planDetails.finalPrice} />
          <div className="col-span-3 flex flex-col gap-2">
            <p className="text-[11px] font-medium leading-4 tracking-[0.5px] text-on-surface-variant">
              Applied Offer
            </p>
            {planDetails.offer ? (
              <div className="flex w-fit max-w-full flex-wrap items-center gap-2 rounded-2xl bg-information-90 px-3 py-1">
                <span className="min-w-0 break-words text-[12px] font-medium leading-4 tracking-[0.5px] text-information-30">
                  {planDetails.offer.name}
                </span>
                <span className="h-3 w-px shrink-0 bg-information-30" />
                <span className="text-[12px] font-medium leading-4 tracking-[0.5px] text-information-30">
                  {planDetails.offer.discountLabel}
                </span>
              </div>
            ) : (
              <p className="text-[16px] font-normal leading-6 text-neutral-10">
                —
              </p>
            )}
          </div>
        </div>
        <div className="rounded-[8px] bg-neutral-95 p-2">
          <SummaryField
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DESCRIPTION.polish}
            value={planDetails.description || '—'}
          />
        </div>
      </Accordion>

      <Accordion
        badgeNumber="02"
        title={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish}
        testId="preview-services"
      >
        {services.map((service, index) => (
          <div
            key={service.key}
            className={`flex flex-col gap-4 ${
              index > 0 ? 'border-t border-neutral-73 pt-6' : ''
            }`}
          >
            <div className="flex flex-nowrap gap-4">
              <SummaryField label="Service Name" value={service.serviceName} />
              <SummaryField
                label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish}
                value={service.basePrice}
              />
              <SummaryField label="Discount" value={service.discountLabel} />
              <SummaryField
                label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_USAGE_LIMITS.polish}
                value={service.usageLimit}
              />
            </div>
            <div className="flex flex-nowrap gap-4">
              <SummaryField label="Final Price" value={service.finalPrice} />
            </div>
          </div>
        ))}
      </Accordion>

      <Accordion
        badgeNumber="03"
        title={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_EQUIPMENT_PRICING.polish}
        testId="preview-equipment-pricing"
      >
        {equipmentCategories.map((category) => (
          <div key={category.id} className="flex flex-wrap gap-6">
            <SummaryField
              label="Equipment Category"
              value={category.categoryLabel}
            />
            <SummaryField
              label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish}
              value={category.basePrice}
            />
          </div>
        ))}
      </Accordion>

      <Accordion
        badgeNumber="04"
        title={SUBSCRIPTION_MANAGEMENT_LABELS.STEP_DISCOUNT_LOGIC.polish}
        testId="preview-discount-logic"
      >
        {discountTiers.map((tier) => (
          <div key={tier.rank} className="flex flex-wrap gap-6">
            <SummaryField
              label="Equipment Quantity"
              value={rankLabel(tier.rank)}
            />
            <SummaryField
              label={
                SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_PLAN_DISCOUNT_PERCENTAGE
                  .polish
              }
              value={tier.discountLabel}
            />
          </div>
        ))}
      </Accordion>
    </div>
  );
};

export default PlanPreviewAccordion;
